import React, { useState } from 'react';
import { 
  Cpu, 
  Factory, 
  Database, 
  PieChart, 
  Zap, 
  ArrowRight, 
  CloudLightning,
  Shield,
  Layers,
  Target,
  Shuffle,
  Maximize
} from 'lucide-react';
import Header from './Header';

export default function BlueGeckoFalcon360() {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);

  // Key features that highlight the AI capabilities
  const keyFeatures = [
    {
      icon: <CloudLightning className="text-blue-400" size={32} />,
      title: "AI-Driven Mapping",
      description: "Intelligently maps complex manufacturing objects (BOMs, routings, materials) with minimal human intervention"
    },
    {
      icon: <Database className="text-purple-400" size={32} />,
      title: "Delta Load Automation",
      description: "Smart automation for ongoing changes during hypercare, ensuring continuous data integrity"
    },
    {
      icon: <Shield className="text-green-400" size={32} />,
      title: "Validation Framework",
      description: "Comprehensive validation for UAT-ready staging across production sites with intelligent error detection"
    },
    {
      icon: <Layers className="text-teal-400" size={32} />,
      title: "System Interoperability",
      description: "Seamless integration with supplier portals, legacy ERPs, and custom manufacturing tools"
    }
  ];

  // Business benefits
  const keyBenefits = [
    {
      icon: <Zap className="text-yellow-400" size={32} />,
      title: "Accelerated Timeline",
      description: "60-80% faster data migration timelines through AI-powered automation and optimization"
    },
    {
      icon: <Target className="text-red-400" size={32} />,
      title: "Reduced Manual Effort",
      description: "Minimize tedious manual mapping and error-prone processes through intelligent automation"
    },
    {
      icon: <Shuffle className="text-indigo-400" size={32} />,
      title: "Seamless Go-Live",
      description: "Experience smooth transitions across all manufacturing plants with minimal disruption"
    },
    {
      icon: <Maximize className="text-emerald-400" size={32} />,
      title: "Lower Risk",
      description: "Enhanced data integrity for production continuity, reducing costly errors and downtime"
    }
  ];

  return (
        <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
    <div
      className="relative bg-gradient-to-b from-transparent via-blue-900 to-blue-900 min-h-screen text-white overflow-hidden"
      style={{ backgroundColor: 'rgba(2, 6, 23, 0.9)' }}
    >
      {/* Animated Background Particles */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {Array.from({ length: 100 }).map((_, i) => {
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const size = Math.random() * 5 + 2;
          return (
            <div
              key={`particle-${left}-${top}-${size}`}
              className="absolute rounded-full bg-blue-500"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${size}px`,
                height: `${size}px`,
                opacity: Math.random() * 0.5,
                animation: `float ${Math.random() * 20 + 10}s linear infinite`,
                animationDelay: `${Math.random() * 10}s`
              }}
            />
          );
        })}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 relative">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="bg-blue-600 bg-opacity-30 p-3 rounded-full">
              <Cpu size={40} className="text-blue-300 animate-pulse" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                BlueGecko Falcon Mapping
              </h1>
              <div className="flex items-center justify-center gap-2 mt-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <p className="text-blue-200">Accelerating Manufacturing System Migrations with Intelligence</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-900 bg-opacity-30 backdrop-blur-sm w-fit mx-auto px-6 py-2 rounded-full border border-blue-500 border-opacity-30 mb-8">
            <span className="text-blue-200">Powered by Advanced AI Technology</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-12">
          {[
            { key: 'overview', label: 'Why It Matters' },
            { key: 'features', label: 'How It Works' },
            { key: 'benefits', label: 'Business Outcomes' }
          ].map((tab) => (
            <button
              key={tab.key}
              className={`
                px-6 py-3 mx-2 rounded-lg transition-all duration-300
                ${activeSection === tab.key 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-900/50' 
                  : 'bg-gray-800 bg-opacity-50 text-blue-200 hover:bg-gray-700 hover:bg-opacity-50'}
              `}
              onClick={() => setActiveSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        {activeSection === 'overview' && (
          <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-blue-500 border-opacity-30 shadow-xl">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent flex items-center">
              <Factory size={24} className="mr-2 text-blue-400" />
              Why It Matters in Manufacturing
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-blue-800 border-opacity-30 hover:border-blue-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-blue-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-blue-200 font-medium mb-2">Legacy System Limitations</h3>
                  <p className="text-gray-300">Legacy systems (like SAP ECC, Oracle EBS) slow down operational agility and prevent manufacturers from implementing modern processes</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-blue-800 border-opacity-30 hover:border-blue-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-blue-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-blue-200 font-medium mb-2">Integration Complexity</h3>
                  <p className="text-gray-300">Frequent integration needs with MES, SCADA, PLM, and supplier systems create data silos and inefficient workflows</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-blue-800 border-opacity-30 hover:border-blue-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-blue-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-blue-200 font-medium mb-2">Data Volume and Complexity</h3>
                  <p className="text-gray-300">High volume master and transactional data: BOMs, routings, material masters, work centers, and production orders create migration challenges</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'features' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
              <Cpu size={24} className="mr-2 text-blue-400" />
              How Falcon Mapping Supports Manufacturing
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature) => (
                <button 
                  key={feature.title}
                  className={`
                    p-6 rounded-xl transition-all duration-300 text-left
                    ${activeFeature === feature.title 
                      ? 'bg-gray-800 border-2 border-blue-500 shadow-2xl' 
                      : 'bg-gray-900 bg-opacity-50 border border-blue-800 border-opacity-30'}
                  `}
                  onMouseEnter={() => setActiveFeature(feature.title)}
                  onMouseLeave={() => setActiveFeature(null)}
                  onFocus={() => setActiveFeature(feature.title)}
                  onBlur={() => setActiveFeature(null)}
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-900 bg-opacity-30 p-3 rounded-full">
                      {feature.icon}
                    </div>
                    <h3 className="ml-4 text-xl font-semibold text-blue-200">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-gray-300">
                    {feature.description}
                  </p>
                </button>
              ))}
            </div>
            
            <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl p-6 border border-blue-500 border-opacity-30">
              <h3 className="text-xl font-semibold text-blue-300 mb-4">Pre-built Templates</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-blue-800 border-opacity-30">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-blue-200">SAP S/4HANA</span>
                  </div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-blue-800 border-opacity-30">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <span className="text-blue-200">Oracle Fusion</span>
                  </div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-blue-800 border-opacity-30">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-blue-200">MES Connectors</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'benefits' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
              <PieChart size={24} className="mr-2 text-blue-400" />
              Business Outcomes
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {keyBenefits.map((benefit) => (
                <button 
                  key={benefit.title}
                  className={`
                    p-6 rounded-xl transition-all duration-300 relative overflow-hidden text-left
                    ${activeBenefit === benefit.title 
                      ? 'bg-gradient-to-br from-blue-900 to-blue-800 border-2 border-blue-500 shadow-2xl' 
                      : 'bg-gray-900 bg-opacity-50 border border-blue-800 border-opacity-30'}
                  `}
                  onMouseEnter={() => setActiveBenefit(benefit.title)}
                  onMouseLeave={() => setActiveBenefit(null)}
                  onFocus={() => setActiveBenefit(benefit.title)}
                  onBlur={() => setActiveBenefit(null)}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-10 rounded-full blur-xl"></div>
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-900 bg-opacity-30 p-3 rounded-full">
                      {benefit.icon}
                    </div>
                    <h4 className="ml-4 text-xl font-semibold text-blue-200">
                      {benefit.title}
                    </h4>
                  </div>
                  <p className="text-gray-300 relative z-10">
                    {benefit.description}
                  </p>
                </button>
              ))}
            </div>
            
            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-8 rounded-xl border border-blue-500 border-opacity-30 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Manufacturing Systems?</h3>
              <p className="text-blue-200 mb-6">
                Experience the power of AI-driven migration with BlueGecko Falcon Mapping and accelerate your digital transformation journey.
              </p>
              <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group">
                Request a Demo
                <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-blue-900 bg-opacity-30 px-4 py-2 rounded-full text-xs flex items-center gap-2 border border-blue-500 border-opacity-20">
            <span className="h-2 w-2 bg-blue-400 rounded-full animate-pulse"></span>
            <span className="text-blue-300">Powered by BlueGecko AI Technology</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}